import { useEffect, useMemo, useState } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'

import { useDashboardFeatureFlags } from '@components'
import { useModalContext } from '@fourthwall/components'

import { AddSubscriptionModal } from '../modules/AddSubscriptionModal/AddSubscriptionModal'
import { useSubscriptionPlansQuery } from '../queries/useSubscriptionPlansQuery'

type PlanParam = 'yearly' | 'monthly'

export const useOpenSubscriptionModal = () => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { open } = useModalContext()
  const [planParam, setPlanParam] = useQueryParam('saas_plan', StringParam)
  const isValidPlanParam = planParam && ['yearly', 'monthly'].includes(planParam)
  const subscriptionsEnabled = dashboardFeatureFlags.subscriptions.enabled
  const [subscriptionModalType, setSubscriptionModalType] = useState<PlanParam | null>(
    isValidPlanParam ? (planParam as PlanParam) : null,
  )

  const { subscriptionPlansQuery } = useSubscriptionPlansQuery()

  const planIds = useMemo(() => {
    const nonDefaultPlans = subscriptionPlansQuery.data?.filter((plan) => plan.tag !== 'DEFAULT')

    return {
      monthly: nonDefaultPlans?.find((plan) => plan.recurringPeriod === 'MONTHLY')?.id,
      yearly: nonDefaultPlans?.find((plan) => plan.recurringPeriod === 'YEARLY')?.id,
    }
  }, [subscriptionPlansQuery.data])

  const initialPlanId = useMemo(() => {
    return subscriptionModalType === 'yearly' ? planIds.yearly : planIds.monthly
  }, [subscriptionModalType, planIds.yearly, planIds.monthly])

  const shouldOpenModal = subscriptionModalType && subscriptionsEnabled && initialPlanId

  useEffect(() => {
    if (shouldOpenModal) {
      open(AddSubscriptionModal, {
        initialStage: 'SELECT_PLAN',
        initialPlanId,
        source: 'external',
      })

      if (planParam) {
        setPlanParam(undefined)
        setSubscriptionModalType(null)
      }
    }
  }, [shouldOpenModal, open, initialPlanId, planParam, setPlanParam])
}
